/**
 * Gatsby browser APIs — tasteful scroll-reveal motion.
 * Elements with the `.reveal` class fade/slide in as they enter the viewport.
 * Safe by design:
 *   - content is only hidden when JS is present (`.has-js` gate), so no-JS users see everything
 *   - honours `prefers-reduced-motion`
 */

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const revealAll = () => {
  if (typeof document === "undefined") return
  const els = document.querySelectorAll(".reveal:not(.is-visible)")
  els.forEach((el) => el.classList.add("is-visible"))
}

let observer = null

const setupReveal = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return

  // tear down the previous page's observer so detached .reveal nodes that never
  // entered the viewport aren't kept alive — otherwise memory climbs across
  // client-side navigations and eventually freezes scrolling on mobile Safari.
  if (observer) {
    observer.disconnect()
    observer = null
  }

  const els = document.querySelectorAll(".reveal:not(.is-visible)")
  if (!els.length) return

  if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
    revealAll()
    return
  }

  observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible")
          obs.unobserve(entry.target)
        }
      })
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
  )

  els.forEach((el) => observer.observe(el))
}

export const onClientEntry = () => {
  if (typeof document !== "undefined") {
    document.documentElement.classList.add("has-js")
  }
}

export const onInitialClientRender = () => {
  // let the DOM settle, then arm the observer
  setTimeout(setupReveal, 1)
}

// Failsafe: if a route change ever interrupts UIkit's offcanvas teardown, its
// page-lock survives React's re-render (uk-offcanvas-page + inline
// overflow-y:hidden on <html>) and the new page can't be scrolled on iOS.
const unlockOffcanvasLeftovers = () => {
  if (typeof document === "undefined") return
  if (document.querySelector(".uk-offcanvas.uk-open")) return // genuinely open — leave it
  const html = document.documentElement
  html.classList.remove("uk-offcanvas-page")
  html.style.removeProperty("overflow-y")
  document.body.classList.remove(
    "uk-offcanvas-container",
    "uk-offcanvas-flip",
    "uk-offcanvas-container-animation"
  )
  document.body.style.removeProperty("touch-action")
}

export const onRouteUpdate = () => {
  // new page mounted via client-side navigation — re-arm for its elements
  unlockOffcanvasLeftovers()
  setTimeout(setupReveal, 1)
}
